/**
 * Company package_modules JSON matches Laravel / PackageSeeder:
 * e.g. {"1":"Reports","2":"POS","3":"Employee Leave","4":"Employee Schedule","5":"Google Calendar","6":"Zoom Meeting"}
 * When we cannot read a list (no package / parse error), gating is skipped so older accounts keep full UI.
 */

export function getEnabledPackageModuleLabels(user: unknown): Set<string> | null {
  const pkg = (user as { company?: { package?: { package_modules?: unknown } } })?.company?.package;
  const raw = pkg?.package_modules;
  if (raw == null || raw === "") return null;

  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (parsed == null || typeof parsed !== "object") return null;
    const values: unknown[] = Array.isArray(parsed)
      ? parsed
      : Object.values(parsed as Record<string, unknown>);
    const labels = values
      .filter((v): v is string => typeof v === "string")
      .map((v) => v.trim())
      .filter(Boolean);
    return new Set(labels);
  } catch {
    return null;
  }
}

export function getEnabledUserModuleLabels(user: unknown): Set<string> | null {
  const raw = (user as { modules?: unknown })?.modules;
  if (!Array.isArray(raw)) return null;

  const labels = raw
    .filter((v): v is string => typeof v === "string")
    .map((v) => v.trim())
    .filter(Boolean);

  if (labels.length === 0) return null;
  return new Set(labels);
}

/** If no parsed list, allow everything (backward compatible). */
export function hasPackageModuleAccess(user: unknown, moduleLabel: string): boolean {
  const packageSet = getEnabledPackageModuleLabels(user);
  const userSet = getEnabledUserModuleLabels(user);

  const packageAllowed = packageSet == null ? true : packageSet.has(moduleLabel);
  const userAllowed = userSet == null ? true : userSet.has(moduleLabel);

  return packageAllowed && userAllowed;
}

/** Main sidebar item id → required package module label (must exist in company.package.package_modules). */
export const SIDEBAR_ITEM_PACKAGE_MODULE: Record<string, string> = {
  dashboard: "Dashboard",
  calendar: "Calendar",
  bookings: "Bookings",
  website: "Website",
  marketing: "Marketing",
  payments: "Manage",
  manage: "Manage",
  customers: "Customers",
  sunbeds: "Sunbeds",
  "sunbed-session": "Sunbed Session",
  reports: "Reports",
  settings: "Settings",
  pos: "POS",
  consultation: "Zoom Meeting",
};

export function isSidebarItemAllowedByPackage(user: unknown, sidebarItemId: string): boolean {
  const required = SIDEBAR_ITEM_PACKAGE_MODULE[sidebarItemId];
  if (!required) return true;
  return hasPackageModuleAccess(user, required);
}

type RouteRule = { prefix: string; module: string };

const ROUTE_PACKAGE_RULES: RouteRule[] = [
  { prefix: "/employee/dashboard", module: "Dashboard" },
  { prefix: "/dashboard", module: "Dashboard" },
  { prefix: "/calendar", module: "Calendar" },
  { prefix: "/bookings", module: "Bookings" },
  { prefix: "/website", module: "Website" },
  { prefix: "/marketing", module: "Marketing" },
  { prefix: "/payments", module: "Manage" },
  { prefix: "/manage", module: "Manage" },
  { prefix: "/customers", module: "Customers" },
  { prefix: "/appointments", module: "Customers" },
  { prefix: "/sunbeds", module: "Sunbeds" },
  { prefix: "/sunbed-session", module: "Sunbed Session" },
  { prefix: "/sunbed-sessions-kiosk", module: "Sunbed Session" },
  { prefix: "/settings", module: "Settings" },
  { prefix: "/reports", module: "Reports" },
  { prefix: "/cashup", module: "Reports" },
  { prefix: "/daily-sales", module: "Reports" },
  { prefix: "/pos", module: "POS" },
  { prefix: "/consultation-response", module: "Zoom Meeting" },
  { prefix: "/consultation", module: "Zoom Meeting" },
  { prefix: "/manage/employee/scheduled-shifts", module: "Employee Schedule" },
  { prefix: "/manage/employee/timesheets", module: "Employee Schedule" },
];

const sortedRouteRules = [...ROUTE_PACKAGE_RULES].sort(
  (a, b) => b.prefix.length - a.prefix.length
);

export function getRequiredPackageModuleForPath(pathname: string): string | null {
  for (const { prefix, module } of sortedRouteRules) {
    if (pathname.startsWith(prefix)) return module;
  }
  return null;
}

export function isPosDefaultScreenEnabled(user: unknown): boolean {
  const val = (user as { company?: { pos_default_screen?: unknown } })?.company?.pos_default_screen;
  return val === true || val === 1 || val === "1" || val === "active";
}

export function getDefaultLandingPath(user: unknown): string {
  const roles = (user as { roles?: { name?: string }[] })?.roles || [];
  const isSuperAdmin = roles.some((r) => r?.name === "superadmin" || r?.name === "agent");
  const isEmployee = roles.some((r) => r?.name === "employee");

  if (isSuperAdmin) return "/superadmin/dashboard";
  if (isEmployee) return "/employee/dashboard";
  if (isPosDefaultScreenEnabled(user) && hasPackageModuleAccess(user, "POS")) {
    return "/pos";
  }
  return "/dashboard";
}
